import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, Filter, Search, TrendingUp, Users, Award,
  Building, CheckCircle, Clock, X, RefreshCw, FileSpreadsheet, FileJson,
  BookOpen, Layers, ChevronDown, ChevronUp, Printer, Eye, FileType, File
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('data'); // 'data' or 'content'
  const [selectedReportType, setSelectedReportType] = useState('organizations');
  const [dateRange, setDateRange] = useState('allTime');
  const [exportFormat, setExportFormat] = useState('json');
  const [filters, setFilters] = useState({
    status: 'all',
    certification_tier: 'all',
    role: 'all'
  });
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Pillar Report States
  const [pillarReportFormat, setPillarReportFormat] = useState('short');
  const [selectedPillars, setSelectedPillars] = useState([]);
  const [selectedSubPillars, setSelectedSubPillars] = useState({});
  const [pillarList, setPillarList] = useState([]);
  const [includeSubPillars, setIncludeSubPillars] = useState(true);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [pillarReportType, setPillarReportType] = useState('all_pillars'); // all_pillars, selected_pillars, subpillars_only

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchReportHistory();
    fetchPillarList();
  }, []);

  const fetchReportHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reports/history`);
      if (response.ok) {
        const data = await response.json();
        setReportHistory(data);
      }
    } catch (error) {
      console.error('Error fetching report history:', error);
    }
  };

  const fetchPillarList = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reports/pillar-list`);
      if (response.ok) {
        const data = await response.json();
        setPillarList(data);
      }
    } catch (error) {
      console.error('Error fetching pillar list:', error);
    }
  };

  const reportTypes = [
    {
      id: 'organizations',
      name: 'Organizations Report',
      description: 'All organizations with status and certification details',
      icon: Building,
      color: 'blue'
    },
    {
      id: 'users',
      name: 'Users Report',
      description: 'Complete user list by role and status',
      icon: Users,
      color: 'green'
    },
    {
      id: 'assessments',
      name: 'Assessments Report',
      description: 'Assessment progress and completion data',
      icon: FileText,
      color: 'purple'
    },
    {
      id: 'certifications',
      name: 'Certifications Report',
      description: 'Certified organizations by tier',
      icon: Award,
      color: 'orange'
    }
  ];

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: selectedReportType,
          date_range: dateRange,
          format: exportFormat,
          filters: filters
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data.report);
        setShowPreview(true);
        fetchReportHistory();
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePillarReport = async () => {
    try {
      setGenerating(true);
      
      let url = `${BACKEND_URL}/api/admin/reports/pillars?report_format=${pillarReportFormat}&include_subpillars=${includeSubPillars}`;
      
      if (pillarReportType === 'selected_pillars' && selectedPillars.length > 0) {
        url += `&pillar_ids=${selectedPillars.join(',')}`;
      } else if (pillarReportType === 'subpillars_only') {
        // Use subpillars endpoint
        const pillarId = selectedPillars.length === 1 ? selectedPillars[0] : 0;
        url = `${BACKEND_URL}/api/admin/reports/subpillars?report_format=${pillarReportFormat}&pillar_id=${pillarId}`;
        
        if (selectedSubPillars[pillarId] && selectedSubPillars[pillarId].length > 0) {
          url += `&subpillar_ids=${selectedSubPillars[pillarId].join(',')}`;
        }
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        setShowPreview(true);
      } else {
        alert('Failed to generate pillar report');
      }
    } catch (error) {
      console.error('Error generating pillar report:', error);
      alert('Error generating pillar report');
    } finally {
      setGenerating(false);
    }
  };

  const handlePillarSelection = (pillarId) => {
    setSelectedPillars(prev => 
      prev.includes(pillarId) 
        ? prev.filter(id => id !== pillarId)
        : [...prev, pillarId]
    );
  };

  const handleSubPillarSelection = (pillarId, subPillarId) => {
    setSelectedSubPillars(prev => {
      const current = prev[pillarId] || [];
      const updated = current.includes(subPillarId)
        ? current.filter(id => id !== subPillarId)
        : [...current, subPillarId];
      return { ...prev, [pillarId]: updated };
    });
  };

  const selectAllPillars = () => {
    setSelectedPillars(pillarList.map(p => p.id));
  };

  const clearAllPillars = () => {
    setSelectedPillars([]);
    setSelectedSubPillars({});
  };

  const handleDownload = () => {
    if (!reportData) return;
    
    if (exportFormat === 'pdf') {
      handleDownloadPDF();
      return;
    }
    
    if (exportFormat === 'doc') {
      handleDownloadDOC();
      return;
    }
    
    let content, filename, type;
    
    if (exportFormat === 'json') {
      content = JSON.stringify(reportData, null, 2);
      filename = `${reportData.report_type || 'report'}_${new Date().toISOString().split('T')[0]}.json`;
      type = 'application/json';
    } else if (exportFormat === 'csv') {
      const data = reportData.data || [];
      if (data.length > 0) {
        const headers = Object.keys(data[0]);
        const csvRows = [
          headers.join(','),
          ...data.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
        ];
        content = csvRows.join('\n');
      } else {
        content = 'No data';
      }
      filename = `${reportData.report_type || 'report'}_${new Date().toISOString().split('T')[0]}.csv`;
      type = 'text/csv';
    } else if (exportFormat === 'txt') {
      // Plain text format for pillar reports
      let textContent = `${reportData.summary?.report_title || 'CNESS Report'}\n`;
      textContent += `Generated: ${new Date().toLocaleString()}\n`;
      textContent += `${'='.repeat(80)}\n\n`;
      
      (reportData.data || []).forEach(item => {
        if (item.pillar_name) {
          textContent += `\nPILLAR ${item.pillar_number}: ${item.pillar_name}\n`;
          textContent += `${'-'.repeat(60)}\n`;
          textContent += `${item.pillar_description}\n`;
          
          if (item.sub_pillars) {
            item.sub_pillars.forEach(sp => {
              textContent += `\n  ${sp.sub_pillar_number}. ${sp.sub_pillar_name}\n`;
              textContent += `  ${sp.sub_pillar_description}\n`;
            });
          }
        } else if (item.sub_pillar_name) {
          textContent += `\n${item.sub_pillar_number}. ${item.sub_pillar_name}\n`;
          textContent += `(Pillar: ${item.pillar_name})\n`;
          textContent += `${item.sub_pillar_description}\n`;
        }
      });
      
      content = textContent;
      filename = `pillars_report_${new Date().toISOString().split('T')[0]}.txt`;
      type = 'text/plain';
    }
    
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // PDF Download Handler
  const handleDownloadPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(79, 70, 229); // Indigo color
    doc.text(reportData.summary?.report_title || 'CNESS Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
    
    // Subtitle
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128); // Gray color
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    
    if (reportData.summary?.total_pillars) {
      doc.text(`Total Pillars: ${reportData.summary.total_pillars} | Total Sub-Pillars: ${reportData.summary.total_sub_pillars || 0}`, pageWidth / 2, yPosition, { align: 'center' });
    }
    yPosition += 15;
    
    // Line separator
    doc.setDrawColor(79, 70, 229);
    doc.setLineWidth(0.5);
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    yPosition += 10;
    
    // Content
    (reportData.data || []).forEach((item, index) => {
      // Check if we need a new page
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
      }
      
      if (item.pillar_name && item.pillar_number) {
        // Pillar Header
        doc.setFontSize(14);
        doc.setTextColor(124, 58, 237); // Purple color
        doc.setFont(undefined, 'bold');
        doc.text(`PILLAR ${item.pillar_number}: ${item.pillar_name}`, 14, yPosition);
        yPosition += 8;
        
        // Pillar Description
        doc.setFontSize(10);
        doc.setTextColor(55, 65, 81); // Dark gray
        doc.setFont(undefined, 'normal');
        const descLines = doc.splitTextToSize(stripHtml(item.pillar_description || ''), pageWidth - 28);
        descLines.slice(0, 5).forEach(line => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
        
        // Sub-pillars
        if (item.sub_pillars && item.sub_pillars.length > 0) {
          item.sub_pillars.forEach((sp) => {
            if (yPosition > 250) {
              doc.addPage();
              yPosition = 20;
            }
            
            // Sub-pillar name
            doc.setFontSize(11);
            doc.setTextColor(79, 70, 229);
            doc.setFont(undefined, 'bold');
            doc.text(`${sp.sub_pillar_number}. ${sp.sub_pillar_name}`, 20, yPosition);
            yPosition += 6;
            
            // Sub-pillar description
            doc.setFontSize(9);
            doc.setTextColor(75, 85, 99);
            doc.setFont(undefined, 'normal');
            const spDescLines = doc.splitTextToSize(stripHtml(sp.sub_pillar_description || ''), pageWidth - 40);
            spDescLines.slice(0, 4).forEach(line => {
              if (yPosition > 275) {
                doc.addPage();
                yPosition = 20;
              }
              doc.text(line, 24, yPosition);
              yPosition += 4;
            });
            yPosition += 4;
          });
        }
        yPosition += 8;
      } else if (item.sub_pillar_name) {
        // Sub-pillar only report
        doc.setFontSize(12);
        doc.setTextColor(79, 70, 229);
        doc.setFont(undefined, 'bold');
        doc.text(`${item.sub_pillar_number}. ${item.sub_pillar_name}`, 14, yPosition);
        yPosition += 6;
        
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text(`Pillar: ${item.pillar_name}`, 14, yPosition);
        yPosition += 6;
        
        doc.setFontSize(10);
        doc.setTextColor(55, 65, 81);
        doc.setFont(undefined, 'normal');
        const spDescLines = doc.splitTextToSize(stripHtml(item.sub_pillar_description || ''), pageWidth - 28);
        spDescLines.slice(0, 5).forEach(line => {
          if (yPosition > 275) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 8;
      }
    });
    
    // Footer on each page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(`CNESS Conscious Business Assessment - Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    }
    
    // Save
    const filename = `${reportData.report_type || 'pillars'}_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  // DOC Download Handler
  const handleDownloadDOC = async () => {
    if (!reportData) return;
    
    const children = [];
    
    // Title
    children.push(
      new Paragraph({
        text: reportData.summary?.report_title || 'CNESS Report',
        heading: HeadingLevel.TITLE,
        spacing: { after: 200 }
      })
    );
    
    // Meta info
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Generated: ${new Date().toLocaleString()}`,
            size: 20,
            color: '6B7280'
          }),
          new TextRun({
            text: reportData.summary?.total_pillars ? ` | Total Pillars: ${reportData.summary.total_pillars} | Total Sub-Pillars: ${reportData.summary.total_sub_pillars || 0}` : '',
            size: 20,
            color: '6B7280'
          })
        ],
        spacing: { after: 400 }
      })
    );
    
    // Content
    (reportData.data || []).forEach((item) => {
      if (item.pillar_name && item.pillar_number) {
        // Pillar Header
        children.push(
          new Paragraph({
            text: `PILLAR ${item.pillar_number}: ${item.pillar_name}`,
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 }
          })
        );
        
        // Pillar Description
        children.push(
          new Paragraph({
            text: stripHtml(item.pillar_description || 'No description available'),
            spacing: { after: 200 }
          })
        );
        
        // Sub-pillars
        if (item.sub_pillars && item.sub_pillars.length > 0) {
          item.sub_pillars.forEach((sp) => {
            children.push(
              new Paragraph({
                text: `${sp.sub_pillar_number}. ${sp.sub_pillar_name}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 }
              })
            );
            
            children.push(
              new Paragraph({
                text: stripHtml(sp.sub_pillar_description || 'No description available'),
                spacing: { after: 200 },
                indent: { left: 720 } // 0.5 inch indent
              })
            );
          });
        }
      } else if (item.sub_pillar_name) {
        // Sub-pillar only report
        children.push(
          new Paragraph({
            text: `${item.sub_pillar_number}. ${item.sub_pillar_name}`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 100 }
          })
        );
        
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Pillar: ${item.pillar_name}`,
                italics: true,
                color: '6B7280'
              })
            ],
            spacing: { after: 100 }
          })
        );
        
        children.push(
          new Paragraph({
            text: stripHtml(item.sub_pillar_description || 'No description available'),
            spacing: { after: 200 }
          })
        );
      }
    });
    
    // Create document
    const doc = new Document({
      sections: [{
        properties: {},
        children: children
      }]
    });
    
    // Generate and save
    const blob = await Packer.toBlob(doc);
    const filename = `${reportData.report_type || 'pillars'}_report_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, filename);
  };

  const handlePrint = () => {
    if (!reportData) return;
    
    const printWindow = window.open('', '_blank');
    let htmlContent = `
      <html>
      <head>
        <title>CNESS Report</title>
        <style>
          body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
          h1 { color: #4F46E5; border-bottom: 3px solid #4F46E5; padding-bottom: 10px; }
          h2 { color: #7C3AED; margin-top: 30px; }
          h3 { color: #6B7280; margin-top: 20px; }
          .pillar { margin-bottom: 40px; page-break-inside: avoid; }
          .sub-pillar { margin-left: 20px; margin-bottom: 20px; padding: 15px; background: #F9FAFB; border-left: 4px solid #4F46E5; }
          .description { color: #374151; }
          .meta { color: #9CA3AF; font-size: 12px; margin-bottom: 20px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <h1>${reportData.summary?.report_title || 'CNESS Report'}</h1>
        <p class="meta">Generated: ${new Date().toLocaleString()} | Format: ${reportData.summary?.report_format || 'N/A'} | Total Pillars: ${reportData.summary?.total_pillars || 0} | Total Sub-Pillars: ${reportData.summary?.total_sub_pillars || 0}</p>
    `;
    
    (reportData.data || []).forEach(item => {
      if (item.pillar_name && item.pillar_number) {
        htmlContent += `
          <div class="pillar">
            <h2>PILLAR ${item.pillar_number}: ${item.pillar_name}</h2>
            <p class="description">${item.pillar_description}</p>
        `;
        
        if (item.sub_pillars && item.sub_pillars.length > 0) {
          item.sub_pillars.forEach(sp => {
            htmlContent += `
              <div class="sub-pillar">
                <h3>${sp.sub_pillar_number}. ${sp.sub_pillar_name}</h3>
                <p class="description">${sp.sub_pillar_description}</p>
              </div>
            `;
          });
        }
        
        htmlContent += `</div>`;
      } else if (item.sub_pillar_name) {
        htmlContent += `
          <div class="sub-pillar">
            <h3>${item.sub_pillar_number}. ${item.sub_pillar_name}</h3>
            <p class="meta">Pillar: ${item.pillar_name}</p>
            <p class="description">${item.sub_pillar_description}</p>
          </div>
        `;
      }
    });
    
    htmlContent += `</body></html>`;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <FileText className="h-10 w-10" />
              <div>
                <h1 className="text-4xl font-black mb-2">Reports</h1>
                <p className="text-lg text-purple-100">Generate and download comprehensive reports</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Report Type Tabs */}
          <div className="flex space-x-4 mb-8">
            <button
              onClick={() => setActiveTab('data')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                activeTab === 'data'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Building className="inline w-5 h-5 mr-2" />
              Data Reports
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 rounded-lg font-bold transition-all ${
                activeTab === 'content'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="inline w-5 h-5 mr-2" />
              Pillar & Sub-Pillar Reports
            </button>
          </div>

          {activeTab === 'data' && (
            <>
              {/* Data Report Types */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {reportTypes.map((report) => {
                  const Icon = report.icon;
                  const isSelected = selectedReportType === report.id;
                  
                  return (
                    <div
                      key={report.id}
                      onClick={() => setSelectedReportType(report.id)}
                      className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all ${
                        isSelected 
                          ? 'ring-4 ring-purple-500 shadow-xl transform scale-105' 
                          : 'hover:shadow-xl hover:scale-102'
                      }`}
                    >
                      <div className={`w-12 h-12 bg-${report.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                        <Icon className={`w-6 h-6 text-${report.color}-600`} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{report.name}</h3>
                      <p className="text-gray-600 text-sm">{report.description}</p>
                      {isSelected && (
                        <div className="mt-3 flex items-center text-purple-600 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Selected
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Data Report Configuration */}
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Report Configuration</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Date Range
                    </label>
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
                    >
                      <option value="today">Today</option>
                      <option value="last7days">Last 7 Days</option>
                      <option value="last30days">Last 30 Days</option>
                      <option value="allTime">All Time</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Download className="inline w-4 h-4 mr-1" />
                      Export Format
                    </label>
                    <select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
                    >
                      <option value="json">JSON (.json)</option>
                      <option value="csv">CSV (.csv)</option>
                      <option value="pdf">PDF (.pdf)</option>
                      <option value="doc">Word (.docx)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Filter className="inline w-4 h-4 mr-1" />
                      Status Filter
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">Report will be generated from live database</p>
                  </div>
                  <button
                    onClick={handleGenerateReport}
                    disabled={generating}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-bold hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        <span>Generate Report</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'content' && (
            <>
              {/* Pillar Report Configuration */}
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  <BookOpen className="inline w-6 h-6 mr-2 text-purple-600" />
                  Pillar & Sub-Pillar Content Report
                </h2>

                {/* Report Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div
                    onClick={() => setPillarReportType('all_pillars')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pillarReportType === 'all_pillars'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Layers className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-bold text-gray-900">All Pillars</p>
                        <p className="text-sm text-gray-600">Complete 6 Pillars report</p>
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => setPillarReportType('selected_pillars')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pillarReportType === 'selected_pillars'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-bold text-gray-900">Selected Pillars</p>
                        <p className="text-sm text-gray-600">Choose specific pillars</p>
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => setPillarReportType('subpillars_only')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pillarReportType === 'subpillars_only'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="font-bold text-gray-900">Sub-Pillars Only</p>
                        <p className="text-sm text-gray-600">Specific sub-pillars report</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Report Format */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Report Format</label>
                    <select
                      value={pillarReportFormat}
                      onChange={(e) => setPillarReportFormat(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
                    >
                      <option value="short">Short (Summary)</option>
                      <option value="detailed">Detailed (Full Content)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Export As</label>
                    <select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
                    >
                      <option value="json">JSON (.json)</option>
                      <option value="csv">CSV (.csv)</option>
                      <option value="txt">Text (.txt)</option>
                      <option value="pdf">PDF (.pdf)</option>
                      <option value="doc">Word (.docx)</option>
                    </select>
                  </div>

                  <div className="flex items-center pt-6">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeSubPillars}
                        onChange={(e) => setIncludeSubPillars(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="font-medium text-gray-700">Include Sub-Pillars</span>
                    </label>
                  </div>
                </div>

                {/* Pillar Selection (for selected_pillars or subpillars_only) */}
                {(pillarReportType === 'selected_pillars' || pillarReportType === 'subpillars_only') && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-semibold text-gray-700">Select Pillars</label>
                      <div className="space-x-2">
                        <button
                          onClick={selectAllPillars}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={clearAllPillars}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
                      {pillarList.map((pillar) => (
                        <div key={pillar.id} className="border rounded-lg overflow-hidden">
                          <div
                            className={`flex items-center justify-between p-3 cursor-pointer transition-all ${
                              selectedPillars.includes(pillar.id) ? 'bg-purple-50' : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                          >
                            <label className="flex items-center space-x-3 flex-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedPillars.includes(pillar.id)}
                                onChange={() => handlePillarSelection(pillar.id)}
                                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="font-medium text-gray-900">
                                {pillar.id}. {pillar.name}
                              </span>
                              <span className="text-sm text-gray-500">
                                ({pillar.sub_pillar_count} sub-pillars)
                              </span>
                            </label>
                            {pillarReportType === 'subpillars_only' && selectedPillars.includes(pillar.id) && (
                              <button
                                onClick={() => setExpandedPillar(expandedPillar === pillar.id ? null : pillar.id)}
                                className="p-1 hover:bg-purple-100 rounded"
                              >
                                {expandedPillar === pillar.id ? (
                                  <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                              </button>
                            )}
                          </div>

                          {/* Sub-pillar selection (expanded) */}
                          {pillarReportType === 'subpillars_only' && expandedPillar === pillar.id && selectedPillars.includes(pillar.id) && (
                            <div className="bg-white border-t p-3 space-y-2">
                              {pillar.sub_pillars.map((sp) => (
                                <label key={sp.id} className="flex items-center space-x-3 cursor-pointer ml-6">
                                  <input
                                    type="checkbox"
                                    checked={(selectedSubPillars[pillar.id] || []).includes(sp.id)}
                                    onChange={() => handleSubPillarSelection(pillar.id, sp.id)}
                                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {pillar.id}.{sp.id}. {sp.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">
                      {pillarReportType === 'all_pillars' && 'Generate complete 6 Pillars report'}
                      {pillarReportType === 'selected_pillars' && `${selectedPillars.length} pillar(s) selected`}
                      {pillarReportType === 'subpillars_only' && 'Generate sub-pillars only report'}
                    </p>
                    <p className="text-gray-400">Format: {pillarReportFormat === 'short' ? 'Summary' : 'Full Content'}</p>
                  </div>
                  <button
                    onClick={handleGeneratePillarReport}
                    disabled={generating || (pillarReportType !== 'all_pillars' && selectedPillars.length === 0)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-bold hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <BookOpen className="w-5 h-5" />
                        <span>Generate Pillar Report</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Report History */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Reports</h3>
            {reportHistory.length > 0 ? (
              <div className="space-y-3">
                {reportHistory.slice(0, 10).map((report, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="font-semibold text-gray-900 capitalize">{report.report_type} Report</p>
                        <p className="text-sm text-gray-500">
                          {report.record_count} records • {new Date(report.generated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                      Generated
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No reports generated yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Report Preview Modal */}
        {showPreview && reportData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold capitalize">
                    {reportData.summary?.report_title || `${reportData.report_type} Report`}
                  </h2>
                  <p className="text-purple-200">
                    {reportData.summary?.total_pillars && `${reportData.summary.total_pillars} Pillars • `}
                    {reportData.summary?.total_sub_pillars && `${reportData.summary.total_sub_pillars} Sub-Pillars • `}
                    {reportData.summary?.total_count && `${reportData.summary.total_count} records • `}
                    {reportData.data?.length || 0} items
                  </p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Summary Stats */}
              {reportData.summary && (
                <div className="p-6 bg-gray-50 border-b">
                  <h3 className="font-bold text-gray-900 mb-3">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {reportData.summary.total_pillars && (
                      <div className="bg-white p-3 rounded-lg border">
                        <p className="text-2xl font-bold text-purple-600">{reportData.summary.total_pillars}</p>
                        <p className="text-sm text-gray-600">Total Pillars</p>
                      </div>
                    )}
                    {reportData.summary.total_sub_pillars && (
                      <div className="bg-white p-3 rounded-lg border">
                        <p className="text-2xl font-bold text-blue-600">{reportData.summary.total_sub_pillars}</p>
                        <p className="text-sm text-gray-600">Sub-Pillars</p>
                      </div>
                    )}
                    {reportData.summary.total_count && (
                      <div className="bg-white p-3 rounded-lg border">
                        <p className="text-2xl font-bold text-green-600">{reportData.summary.total_count}</p>
                        <p className="text-sm text-gray-600">Total Records</p>
                      </div>
                    )}
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-2xl font-bold text-gray-900 capitalize">{reportData.summary.report_format || 'N/A'}</p>
                      <p className="text-sm text-gray-600">Format</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Preview */}
              <div className="p-6 overflow-y-auto max-h-[40vh]">
                <h3 className="font-bold text-gray-900 mb-3">Preview</h3>
                <div className="space-y-4">
                  {(reportData.data || []).slice(0, 5).map((item, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                      {item.pillar_name && item.pillar_number && (
                        <>
                          <h4 className="font-bold text-purple-700 mb-2">
                            Pillar {item.pillar_number}: {item.pillar_name}
                          </h4>
                          <p className="text-gray-600 text-sm mb-3">
                            {item.pillar_description?.substring(0, 200)}...
                          </p>
                          {item.sub_pillars && item.sub_pillars.length > 0 && (
                            <div className="ml-4 space-y-2">
                              {item.sub_pillars.slice(0, 2).map((sp, spIdx) => (
                                <div key={spIdx} className="text-sm border-l-2 border-purple-300 pl-3">
                                  <p className="font-semibold text-gray-800">{sp.sub_pillar_number}. {sp.sub_pillar_name}</p>
                                  <p className="text-gray-500">{sp.sub_pillar_description?.substring(0, 100)}...</p>
                                </div>
                              ))}
                              {item.sub_pillars.length > 2 && (
                                <p className="text-xs text-gray-400 ml-3">+{item.sub_pillars.length - 2} more sub-pillars...</p>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {!item.pillar_number && item.sub_pillar_name && (
                        <>
                          <h4 className="font-bold text-blue-700 mb-1">
                            {item.sub_pillar_number}. {item.sub_pillar_name}
                          </h4>
                          <p className="text-xs text-gray-500 mb-2">Pillar: {item.pillar_name}</p>
                          <p className="text-gray-600 text-sm">{item.sub_pillar_description?.substring(0, 200)}...</p>
                        </>
                      )}
                      {!item.pillar_name && !item.sub_pillar_name && (
                        <pre className="whitespace-pre-wrap text-gray-700 text-sm overflow-hidden">
                          {JSON.stringify(item, null, 2).substring(0, 300)}...
                        </pre>
                      )}
                    </div>
                  ))}
                  {(reportData.data || []).length > 5 && (
                    <p className="text-center text-gray-500 text-sm">
                      +{reportData.data.length - 5} more items in full report
                    </p>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-4 flex justify-between items-center border-t">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Close
                </button>
                <div className="flex space-x-3">
                  {(reportData.report_type === 'pillars' || reportData.report_type === 'subpillars') && (
                    <button
                      onClick={handlePrint}
                      className="px-6 py-2 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-800 transition-all flex items-center space-x-2"
                    >
                      <Printer className="w-5 h-5" />
                      <span>Print</span>
                    </button>
                  )}
                  <button
                    onClick={handleDownload}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:shadow-lg transition-all flex items-center space-x-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download {exportFormat.toUpperCase()}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SystemAdminLayout>
  );
};

export default Reports;
